/**
 * UnverifiedEmailBanner — autenticazione Phase 6 (IMPL-P6-004).
 *
 * Sticky top banner shown to authenticated users whose email has not yet
 * been verified (UC-006). Renders nothing for unauthenticated visitors or
 * for users with `emailVerifiedAt !== null`.
 *
 * Design:
 *  - amber colour palette (Tailwind 50/200/900) — neutral-warning, not error
 *  - sticky top so it stays visible while the user navigates dashboards
 *  - non-dismissible: it disappears only after the email is actually verified
 *
 * The CTA delegates to {@link ResendVerificationButton} which owns the 60s
 * cooldown logic.
 */
import { MailWarning } from 'lucide-react';
import { useAuthSession } from '../hooks/useAuthSession';
import { ResendVerificationButton } from './ResendVerificationButton';

export function UnverifiedEmailBanner() {
  const { user, status } = useAuthSession();

  // Render nothing if not authenticated yet (loading or anonymous) or if the
  // email is already verified.
  if (status !== 'authenticated' || !user) return null;
  if (user.emailVerifiedAt !== null) return null;

  return (
    <div
      role="region"
      aria-label="Avviso email non verificata"
      className="sticky top-0 z-30 w-full border-b border-amber-200 bg-amber-50 text-amber-900"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-start gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 sm:items-center">
          <MailWarning className="h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm leading-snug">
            Per attivare tutte le funzionalità, conferma la tua email. Abbiamo inviato un link a{' '}
            <strong className="font-semibold">{user.email}</strong>.
          </p>
        </div>
        <ResendVerificationButton
          email={user.email}
          className="border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200 hover:text-amber-900"
        />
      </div>
    </div>
  );
}
